import React, { Fragment, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { withTheme } from "styled-components";

import { Button, Checkbox } from "@pearsontechnology/aero-components";
import { ModalContent, ModalActions } from "components/Modal";
import { intl } from "containers/LanguageProvider/IntlGlobalProvider";

import { FormField, CheckBoxContainer, Italic } from "./styles";

import messages from "./messages";
import { cohortCheckboxesName } from "../constants";

import publicationService from "services/PublicationService";
import libraryService from "services/LibraryService";
import enhanceWithFeatureFlagSubscription from "hocs/enhanceWithFeatureFlagSubscription";
import { featureKeys } from "services/FeatureFlagService";

function LockPluginVersionModal({ onDeploy, onDismiss, cohortId, deploymentId, deploymentDetails = {}, activityId, getFeatureFlagValue }) {
  const [checkboxValues, setCheckBoxValues] = useState({
    lockPluginVersion: true,
    supportOnDemandEnabled: false,
    scorable: deploymentDetails?.isScored ? true : false,
    forceUpdateAssessments: false,
    addToLibrary: false,
  });
  const [isAlreadyInLibrary, setIsAlreadyInLibrary] = useState(false);
  const onDemandCheckboxDisabled = deploymentId ? true : false;
  const addToLibraryEnabled = getFeatureFlagValue(featureKeys.BAEX_462_ADD_DEPLOYMENT_TO_LIBRARY);
  
  const handleCheckBoxChange = (event) => {
    const { name, checked } = event.target;
    setCheckBoxValues((preValues) => ({ ...preValues, [name]: checked }));
  };

  const isOnDemandCallback = useCallback(async (cohortId, deploymentId) => {
    const response = await publicationService.getIsOnDemand(
      cohortId,
      deploymentId
    );

    setCheckBoxValues((val) => ({
      ...val,
      supportOnDemandEnabled: response?.isOnDemand ? true : false,
    }));
  }, []);

  useEffect(() => {
    if (cohortId && deploymentId) {
      isOnDemandCallback(cohortId, deploymentId);
    }
  }, [cohortId, deploymentId, isOnDemandCallback]);

  const loadLibraryStatus = useCallback(async (currentActivityId) => {
    if (!currentActivityId) {
      setIsAlreadyInLibrary(false);
      return;
    }

    try {
      const items = await libraryService.getLibraryList();
      const alreadyInLibrary = items.some((item) => item.activityId === currentActivityId);
      setIsAlreadyInLibrary(alreadyInLibrary);

      if (alreadyInLibrary) {
        setCheckBoxValues((prevValues) => ({
          ...prevValues,
          addToLibrary: true,
        }));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching library list for Deploy as Library checkbox:', error);
    }
  }, []);

  useEffect(() => {
    if (!addToLibraryEnabled) {
      setIsAlreadyInLibrary(false);
      return undefined;
    }

    loadLibraryStatus(activityId);
  }, [activityId, addToLibraryEnabled, loadLibraryStatus]);


  return (
    <Fragment>
      <ModalContent>
        <FormField>
          <CheckBoxContainer>
            <Checkbox
              label={intl.formatMessage(
                messages.lockPluginVersionCheckboxLabel
              )}
              name={cohortCheckboxesName.LOCKDOWN_PLUGIN_VERSION}
              checked={checkboxValues.lockPluginVersion}
              value={"plugin"}
              onChange={handleCheckBoxChange}
            />
            <p>{intl.formatMessage(messages.lockPluginVersionDescription)}</p>
            <Italic>
              {intl.formatMessage(messages.lockPluginVersionDescriptionItalic)}
            </Italic>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <Checkbox
              label={intl.formatMessage(
                messages.supportOnDemandClassesCheckboxLabel
              )}
              name={cohortCheckboxesName.SUPPORT_ON_DEMAND_CLASSES}
              checked={checkboxValues.supportOnDemandEnabled}
              onChange={handleCheckBoxChange}
              disabled={onDemandCheckboxDisabled}
            />
          </CheckBoxContainer>
          <CheckBoxContainer>
            <Checkbox
              label={intl.formatMessage(
                messages.scorable
              )}
              name={cohortCheckboxesName.SCORABLE}
              checked={checkboxValues.scorable}
              onChange={handleCheckBoxChange}
              disabled={deploymentDetails.isScored? onDemandCheckboxDisabled : !checkboxValues.supportOnDemandEnabled}
            />
            <Italic>
              {intl.formatMessage(messages.scorableDescriptionItalic)}
            </Italic>
          </CheckBoxContainer>
          <CheckBoxContainer>
            <Checkbox
              label={intl.formatMessage(
                messages.forceUpdateAssessments
              )}
              name={cohortCheckboxesName.FORCE_UPDATE_ASSESSMENTS}
              checked={checkboxValues.forceUpdateAssessments}
              onChange={handleCheckBoxChange}
              disabled={!checkboxValues.supportOnDemandEnabled}
            />
            <Italic>
              {intl.formatMessage(messages.forceUpdateAssessmentsDescriptionItalic)}
            </Italic>
          </CheckBoxContainer>
          { addToLibraryEnabled && <CheckBoxContainer>
            <Checkbox
              label={intl.formatMessage(messages.deployAsLibraryCheckboxLabel)}
              name={cohortCheckboxesName.ADD_TO_LIBRARY}
              checked={isAlreadyInLibrary || checkboxValues.addToLibrary}
              onChange={handleCheckBoxChange}
              disabled={isAlreadyInLibrary}
            />
            <Italic>
              {intl.formatMessage(
                isAlreadyInLibrary
                  ? messages.deployAsLibraryAlreadyInLibraryDescription
                  : messages.deployAsLibraryDescriptionItalic
              )}

            </Italic>
          </CheckBoxContainer> }
        </FormField>
      </ModalContent>
      <ModalActions>
        <Button grey onClick={onDismiss}>
          {intl.formatMessage(messages.cancelButton)}
        </Button>
        <Button primary onClick={() => onDeploy(checkboxValues)}>
          {intl.formatMessage(messages.deployButton)}
        </Button>
      </ModalActions>
    </Fragment>
  );
}

LockPluginVersionModal.propTypes = {
  onDeploy: PropTypes.func,
  onDismiss: PropTypes.func,
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
  activityId: PropTypes.string,
  getFeatureFlagValue: PropTypes.func,
};

export default withTheme(enhanceWithFeatureFlagSubscription(LockPluginVersionModal, [featureKeys.BAEX_462_ADD_DEPLOYMENT_TO_LIBRARY]));
